import React, { useEffect, useRef, useState } from 'react'
import { camelCase, get, isArray, omit } from 'lodash'
import { Navigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { JSONSchema7 } from 'json-schema'

import { apiJSON } from '@shared/utils/api/api.util'
import { Table } from '@shared/components/table/table.component'
import { useRouter } from '@shared/hooks/use-router'
import { ConfirmationDialog } from '@shared/components/confirmation.dialog.component'
import { useSchemasQuery } from '@shared/hooks/use-schemas-query.hook'
import { Empty } from '@shared/components/empty.component'
import { useResourceRemove } from '@shared/hooks/use-resource-remove.hook'
import { originalIcons } from '@shared/components/icons/icons'

import { Resource } from './admin-generic.types'
import { useThemeStyles } from '@shared/theme/use-theme-styles'
import {
  defaultParseResourceData,
  getFinalResourceConfig,
  getOmittedFields,
  getResourceConfig,
  getResourceIdentityProperty,
  getResourceRefSchema,
  getResourceSchemaKeyFromRef,
} from '@modules/admin-generic/admin-generic.utils'
import {
  AdminResourceType,
  CLIENT_ADMIN,
  ClientAdminResourceType,
  GenericResourceData,
  Roles,
} from '@shared/types'
import { ALLOWED_RESOURCES } from '@modules/admin-generic/admin-generic.consts'
import { MaterialTableProps } from '@material-table/core'
import Joyride from 'react-joyride'
import { onboardingConfig } from '@shared/onboarding/config'
import { administrationSteps } from '@shared/onboarding/steps'
import ids from '@shared/onboarding/ids'
import { ACL } from '@shared/components/acl.component'
import {
  OnboardingType,
  useOnboardingStore,
} from '@shared/onboarding/onboarding.store'

type RouteQuery = {
  resourceName: string
  filterBy?: string
  filterValue?: string
}

export const AdminGenericCrudPage = () => {
  const {
    query: { resourceName },
    location,
  } = useRouter<RouteQuery>()
  if (!(ALLOWED_RESOURCES as string[]).includes(resourceName)) {
    return <Navigate to="/admin" />
  }

  return <AdminGenericCrudPageChild />
}

const getDialogUrl = (resourceName: string, resourceId?: string) => {
  if (resourceName.includes(CLIENT_ADMIN)) {
    return `/client-admin/${resourceName.replace(new RegExp(`^${CLIENT_ADMIN}\\.`), '')}${resourceId ? `/edit/${resourceId}` : '/add'}`
  }

  return `/admin/${resourceName}${resourceId ? `/edit/${resourceId}` : '/add'}`
}

export const AdminGenericCrudPageChild = ({
  resourceNameOverride,
}: {
  resourceNameOverride?: AdminResourceType | ClientAdminResourceType
}) => {
  const {
    query: { resourceName: resourceNameQuery, filterBy, filterValue },
    navigate,
    location,
  } = useRouter<RouteQuery>()
  const { isActive, setActive } = useOnboardingStore()
  const [stepIndex, setStepIndex] = useState(0)
  const [readyForStep3, setReadyForStep3] = useState(false)

  const resourceName = resourceNameOverride || resourceNameQuery
  const isUsersPage = resourceName.includes('users')

  const serachText = useRef<string | undefined>(undefined)
  const themeClasses = useThemeStyles()
  const resourceConfig = getResourceConfig(resourceName) ?? ({} as Resource)

  const {
    dto: resourceSchemaKey,
    singular: resourceNameSingular,
    endpoint: resourceEndpoint,
    table: resourceTableConfig,
    parseData: parseResourceData,
    components: resourceTableComponents,
  } = resourceConfig

  const resourceIdentityProperty = getResourceIdentityProperty(
    `${camelCase(resourceSchemaKey)}`
  )

  const { schema } = useSchemasQuery()

  const {
    isRemovalDialogOpened,
    isResourceRemoving,
    setRemovalDialogOpened,
    setRemovedResourceID,
    onResourceRemove,
    isInvalidatingAfterRemove,
  } = useResourceRemove(resourceEndpoint)

  const { data: resourceData, isLoading: isResourceLoading } =
    useQuery<GenericResourceData>(resourceEndpoint, () =>
      apiJSON(resourceEndpoint)
    )

  const resourceSchema =
    schema?.components?.schemas?.[`${resourceSchemaKey}DTO`]

  const resourceSchemaProperties = resourceSchema?.properties ?? {}

  const resourceSchemaRequiredProperties = resourceSchema?.required ?? []

  const resourceFinalSchema = omit(
    resourceSchemaProperties,
    getOmittedFields(
      resourceNameSingular,
      resourceConfig,
      resourceSchemaProperties
    )
  ) as JSONSchema7

  const resourceTitle = resourceSchema?.description ?? resourceNameSingular

  const columns = (
    Object.entries(resourceFinalSchema)
      .map(([property, propertySchema]: [string, JSONSchema7]) => {
        if (resourceTableConfig?.[property]) {
          return resourceTableConfig[property]
        }

        const isCompaniesFieldInUsersTable =
          resourceName === AdminResourceType.USERS && property === 'companies'

        /**
         * @todo implement custom array fields rendering
         */
        if (
          (propertySchema?.type === 'array' && !isCompaniesFieldInUsersTable) ||
          (resourceName === AdminResourceType.LEGAL_BASIS &&
            ['lexApiNRO', 'lexApiURL'].includes(property))
        ) {
          return false
        }

        if (propertySchema?.$ref) {
          const propertyResourceSchemaKey = getResourceSchemaKeyFromRef(
            propertySchema?.$ref
          )

          const $refResourceConfig = getFinalResourceConfig(
            propertyResourceSchemaKey
          )
          const $refSchema = getResourceRefSchema(
            schema?.components?.schemas,
            $refResourceConfig
          )

          if (!$refSchema?.properties?.name) {
            return false
          }

          return {
            field: `${property}.name`,
            title: $refSchema?.description ?? property,
            ...($refResourceConfig.lookup
              ? {
                  lookup: (resourceData ?? []).reduce((memo, item) => {
                    memo[get(item, `${property}.name`) as string] = get(
                      item,
                      `${property}.name`
                    )

                    return memo
                  }, {}),
                }
              : {}),
          }
        }

        return {
          field: property,
          title: propertySchema?.description ?? property,
          ...((propertySchema?.maxLength ?? 0 >= 10000)
            ? { width: '60%' }
            : {}),
        }
      })
      .filter(Boolean) as { field: string; title: string }[]
  ).map((column, index) => {
    const commonStyles = {
      padding: 5,
      ...(index === 0 ? { paddingLeft: 20 } : {}),
    }
    let newColumn
    if (column.field === 'companyHeadquaters') {
      newColumn = {
        ...column,
        customSort: (a: any, b: any) =>
          a['companyHeadquaters'].localeCompare(b['companyHeadquaters'], 'pl'),
      }
    } else {
      newColumn = column
    }

    return {
      ...newColumn,
      cellStyle: commonStyles,
      headerStyle: commonStyles,
    }
  })

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const el = document.querySelector(`#${ids.ADD_USER_TYPE}`)
      if (el) {
        setReadyForStep3(true)
        observer.disconnect()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (readyForStep3) {
      setStepIndex(2)
    }
  }, [readyForStep3])

  const handleJoyrideCallback = (data: { status: string; type: string }) => {
    const { status, type } = data

    if (['finished', 'skipped'].includes(status)) {
      setActive(OnboardingType.ADMINISTRATION, false)
      setStepIndex(0)
      return
    }

    if (type === 'step:after') {
      if (stepIndex === 0) {
        setStepIndex(1)
      } else if (stepIndex === 1) {
        const iconElement = document.querySelector(
          `#${isUsersPage ? ids.ADD_USER : ''}`
        )

        if (iconElement && iconElement instanceof HTMLElement) {
          iconElement.click()
        }
        if (readyForStep3) {
          setStepIndex(2)
        }
      } else if (stepIndex === 2) {
        setStepIndex(3)
      } else if (stepIndex === 3) {
        setActive(OnboardingType.ADMINISTRATION, false)
        setStepIndex(0)
      }
    }
  }

  useEffect(() => {
    if (isActive(OnboardingType.ADMINISTRATION)) {
      setActive(OnboardingType.ADMINISTRATION, false)

      const timer = setTimeout(() => {
        setActive(OnboardingType.ADMINISTRATION, true)
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [])

  return (
    <span className={themeClasses.tableContainer}>
      <ACL
        allowedRoles={[
          Roles.Client,
          Roles.ClientEditor,
          Roles.ClientAdministrator,
          Roles.ClientBasic,
        ]}
      >
        <Joyride
          {...onboardingConfig}
          steps={administrationSteps}
          continuous
          stepIndex={stepIndex}
          run={isActive(OnboardingType.ADMINISTRATION)}
          callback={handleJoyrideCallback}
          styles={{
            options: {
              primaryColor: '#FF6133',
              zIndex: 10000,
            },
          }}
        />
      </ACL>
      <Table<{ [key: string]: string | number }>
        isLoading={isResourceLoading || isInvalidatingAfterRemove}
        data={
          parseResourceData
            ? parseResourceData(resourceData ?? [])
            : defaultParseResourceData(resourceData, {
                filterBy,
                filterValue,
              })
        }
        title={resourceTitle}
        columns={columns}
        options={{
          grouping: true,
          filtering: true,
          searchText: serachText.current,
        }}
        onSearchChange={(value) => (serachText.current = value)}
        components={{
          Groupbar: Empty,
          ...(resourceTableComponents ?? {}),
          FilterRow: ({ columns, onFilterChanged }) => {
            const rearrangedColumns = columns.sort(
              (
                a: { defaultGroupOrder: number },
                b: { defaultGroupOrder: number }
              ) => {
                const aHasOrder = a.defaultGroupOrder !== undefined
                const bHasOrder = b.defaultGroupOrder !== undefined

                if (aHasOrder && !bHasOrder) return -1
                if (!aHasOrder && bHasOrder) return 1
                return 0
              }
            )

            return (
              <tr>
                {rearrangedColumns.map(
                  (col: {
                    field: string
                    defaultGroupOrder: number
                    title: string
                    tableData: { id: number }
                  }) => {
                    if (!col.field) return null

                    const isHidden = col.defaultGroupOrder !== undefined

                    return (
                      <td
                        key={col.field}
                        style={{
                          visibility: isHidden ? 'hidden' : 'visible',
                        }}
                      >
                        <input
                          style={{
                            height: '40px',
                            width: '200px',
                            borderRadius: '4px',
                            fontSize: '1rem',
                            fontFamily: 'Roboto',
                            border: '1px solid lightgrey',
                            padding: '20px',
                            marginTop: '10px',
                          }}
                          id={col.field}
                          placeholder={col.title}
                          onChange={(e) =>
                            onFilterChanged(col.tableData.id, e.target.value)
                          }
                        />
                      </td>
                    )
                  }
                )}
              </tr>
            )
          },
        }}
        actions={
          [
            {
              icon: originalIcons.Create,
              tooltip: 'Edytuj',
              onClick: (
                event: React.MouseEvent<HTMLElement>,
                resourceEntity
              ) => {
                if (!isArray(resourceEntity)) {
                  const url = getDialogUrl(
                    resourceName,
                    resourceEntity[
                      resourceIdentityProperty
                    ] as unknown as string
                  )
                  navigate(url, {
                    state: {
                      backgroundLocation: location,
                    },
                  })
                }
              },
            },
            {
              icon: originalIcons.Delete,
              tooltip: 'Usuń',
              onClick: (
                event: React.MouseEvent<HTMLElement>,
                resourceEntity
              ) => {
                if (!isArray(resourceEntity)) {
                  setRemovedResourceID(
                    resourceEntity[
                      resourceIdentityProperty
                    ] as unknown as string
                  )
                  setRemovalDialogOpened(true)
                }
              },
            },
            {
              icon: () => (
                <div id={isUsersPage ? ids.ADD_USER : ''}>
                  <originalIcons.Add />
                </div>
              ),
              isFreeAction: true,
              onClick: () => {
                navigate(getDialogUrl(resourceName), {
                  state: {
                    backgroundLocation: location,
                  },
                })
              },
            },
          ] as MaterialTableProps<any>['actions']
        }
      />
      <ConfirmationDialog
        submitButtonProps={{
          children: 'Usuń',
          isLoading: isResourceRemoving,
        }}
        title="Potwierdzenie usunięcia"
        onClose={onResourceRemove}
        open={isRemovalDialogOpened}
      >
        Czy napewno chcesz usunąć dany wiersz?
      </ConfirmationDialog>
    </span>
  )
}
